/** Usage Metrics tab — admin analytics dashboard.
 *
 * Renders KPI cards, an active-users trend chart, login frequency
 * distribution, retention cohort table, top-pages chart, and per-page
 * dwell time table. A shared time-range filter (7d/30d/90d) drives
 * every metric.
 */

import {
    AlertCircle,
    BarChart3,
    CalendarClock,
    MousePointerClick,
    RefreshCw,
    Timer,
    TrendingUp,
    Users,
    type LucideIcon,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { DateRangePicker } from "@/shared/ui/date-range-picker";
import { T } from "@/shared/ui/DesignLib";
import { PAGE_SIZE_OPTIONS, PaginationControls } from "@/shared/ui/pagination-controls";
import { Skeleton } from "@/shared/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/shared/ui/table";

import {
    getTimezone,
    getTimezoneLabel,
} from "@/shared/lib/timezone";
import { useMetrics } from "../hooks/useMetrics";
import {
    dateRangeToMetricsRange,
    isCustomDateRange,
    metricsRangeToDateRange,
    type MetricsRange,
    type TimeRange,
} from "../lib/metricsTypes";
import MetricCard from "./MetricCard";

const RANGE_LABELS: Record<TimeRange, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

function rangeLabel(range: MetricsRange): string {
  if (isCustomDateRange(range)) return `${range.from} – ${range.to}`;
  return RANGE_LABELS[range];
}

const PAGE_BAR_COLORS = [T.primary, T.purple, T.amber, T.green, "#38bdf8", "#fb7185"];

const AXIS_STYLE = {
  fontSize: 10,
  fontFamily: "var(--font-mono)",
  fill: "var(--muted-foreground)",
} as const;

const TOOLTIP_STYLE = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  color: "var(--foreground)",
} as const;

const TOOLTIP_ITEM_STYLE = {
  color: "var(--foreground)",
} as const;

const TOOLTIP_LABEL_STYLE = {
  color: "var(--muted-foreground)",
} as const;

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: readonly any[]; label?: string | number }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={TOOLTIP_STYLE}>
      {label && <p style={{ ...TOOLTIP_LABEL_STYLE, margin: 0, paddingBottom: 4 }}>{label}</p>}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {payload.map((item, i) => (
          <li key={i} style={{ ...TOOLTIP_ITEM_STYLE, display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: item.color ?? "var(--foreground)",
                flexShrink: 0,
              }}
            />
            <span>
              {item.name ?? item.dataKey} : {item.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return remainder === 0 ? `${minutes}m` : `${minutes}m ${remainder}s`;
}

function formatDateLabel(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: getTimezone(),
  });
}

function truncatePath(path: string, max = 28): string {
  return path.length > max ? `${path.slice(0, max - 1)}…` : path;
}

function SectionCard({
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <CardTitle className="text-sm font-semibold text-foreground">
            {title}
          </CardTitle>
        </div>
        {subtitle && (
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {subtitle}
          </p>
        )}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-[180px] items-center justify-center rounded-[var(--radius-sm)] border border-dashed border-border">
      <p className="max-w-sm text-center text-[12px] text-muted-foreground">
        {message}
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={104} borderRadius={8} data-testid="metrics-skeleton" />
        ))}
      </div>
      <Skeleton height={320} borderRadius={8} data-testid="metrics-skeleton" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton height={280} borderRadius={8} data-testid="metrics-skeleton" />
        <Skeleton height={280} borderRadius={8} data-testid="metrics-skeleton" />
      </div>
    </div>
  );
}

export interface UsageMetricsTabProps {
  className?: string;
}

export default function UsageMetricsTab({ className }: UsageMetricsTabProps) {
  const {
    activeUsers,
    loginFrequency,
    userRetention,
    pageUsage,
    pageDwellTime,
    loading,
    error,
    range,
    setRange,
    refetch,
  } = useMetrics("30d");

  const trendData = useMemo(
    () =>
      (activeUsers?.points ?? []).map((point) => ({
        ...point,
        label: formatDateLabel(point.date),
      })),
    [activeUsers],
  );

  const topPages = useMemo(
    () =>
      (pageUsage?.pages ?? []).slice(0, 10).map((page) => ({
        ...page,
        label: truncatePath(page.page_path),
      })),
    [pageUsage],
  );

  // Client-side pagination for Page Dwell Time table
  const [dwellPage, setDwellPage] = useState(1);
  const [dwellPageSize, setDwellPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const dwellPages = pageDwellTime?.pages ?? [];
  const dwellTotal = dwellPages.length;
  const dwellTotalPages = Math.ceil(dwellTotal / dwellPageSize) || 0;
  const dwellStart = (dwellPage - 1) * dwellPageSize;
  const dwellPageRows = dwellPages.slice(dwellStart, dwellStart + dwellPageSize);

  return (
    <div className={className}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1
            className="m-0 text-[1.5rem] font-bold leading-[1.1] text-foreground md:text-[2rem]"
            style={{ fontFamily: T.serif }}
          >
            User Metrics
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {rangeLabel(range)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={metricsRangeToDateRange(range)}
            onChange={(dr) => {
              const next = dateRangeToMetricsRange(dr);
              setRange(next ?? "30d");
            }}
            align="end"
            maxDate={new Date()}
          />
          <Button
            variant="outline"
            size="icon-sm"
            onClick={refetch}
            disabled={loading}
            aria-label="Refresh metrics"
          >
            <RefreshCw className={loading ? "animate-spin" : undefined} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="mb-5 border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-start gap-2 p-4">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div>
              <p className="text-[13px] font-medium text-destructive">
                Failed to load metrics
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <LoadingSkeleton />}

      {!loading && !error && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Daily Active"
              value={activeUsers?.summary.current_dau ?? 0}
              hint="Unique logins today"
              icon={Users}
            />
            <MetricCard
              label="Weekly Active"
              value={activeUsers?.summary.current_wau ?? 0}
              hint="Rolling 7-day logins"
              icon={TrendingUp}
            />
            <MetricCard
              label="Monthly Active"
              value={activeUsers?.summary.current_mau ?? 0}
              hint="Rolling 30-day logins"
              icon={CalendarClock}
            />
            <MetricCard
              label="Avg Dwell"
              value={
                pageDwellTime
                  ? formatDuration(pageDwellTime.overall_avg_dwell_seconds)
                  : "—"
              }
              hint="Across all tracked pages"
              icon={Timer}
            />
          </div>

          <SectionCard
            title="Active Users Trend"
            subtitle={`DAU · WAU · MAU (${getTimezoneLabel()})`}
            icon={TrendingUp}
          >
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dauFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={T.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                  <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: "var(--border)" }} />
                  <Area
                    type="monotone"
                    dataKey="dau"
                    name="DAU"
                    stroke={T.primary}
                    strokeWidth={2}
                    fill="url(#dauFill)"
                  />
                  <Area
                    type="monotone"
                    dataKey="wau"
                    name="WAU"
                    stroke={T.purple}
                    strokeWidth={1.5}
                    fill="none"
                  />
                  <Area
                    type="monotone"
                    dataKey="mau"
                    name="MAU"
                    stroke={T.amber}
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    fill="none"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState message="No login activity recorded in this period." />
            )}
          </SectionCard>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SectionCard
              title="Login Frequency"
              subtitle={
                loginFrequency
                  ? `${loginFrequency.total_users} users · ${loginFrequency.avg_logins_per_user} avg`
                  : "Sessions per user"
              }
              icon={BarChart3}
            >
              {loginFrequency && loginFrequency.total_users > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={loginFrequency.buckets}
                    margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                  >
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="bucket" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={ChartTooltip} cursor={{ fill: "var(--secondary)" }} />
                    <Bar dataKey="user_count" name="Users" fill={T.primary} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="No login events recorded in this period." />
              )}
            </SectionCard>

            <SectionCard
              title="Top Pages"
              subtitle={
                pageUsage ? `${pageUsage.total_page_views} total views` : "Most visited pages"
              }
              icon={MousePointerClick}
            >
              {topPages.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={topPages}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={AXIS_STYLE}
                      tickLine={false}
                      axisLine={false}
                      width={140}
                    />
                    <Tooltip content={ChartTooltip} cursor={{ fill: "var(--secondary)" }} />
                    <Bar dataKey="visit_count" name="Visits" radius={[0, 3, 3, 0]}>
                      {topPages.map((page, index) => (
                        <Cell
                          key={page.page_path}
                          fill={PAGE_BAR_COLORS[index % PAGE_BAR_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="No page views yet. Data appears once the tracking beacon reports activity." />
              )}
            </SectionCard>
          </div>

          <SectionCard
            title="Retention Cohorts"
            subtitle={
              userRetention
                ? `Avg week-1 retention ${userRetention.avg_week_1_retention}%`
                : "Weekly cohort retention"
            }
            icon={CalendarClock}
          >
            {userRetention && userRetention.cohorts.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{`Cohort (${getTimezoneLabel()})`}</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead className="text-right">Week 1</TableHead>
                    <TableHead className="text-right">Week 2</TableHead>
                    <TableHead className="text-right">Week 4</TableHead>
                    <TableHead className="text-right">Week 8</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userRetention.cohorts.map((cohort) => (
                    <TableRow key={cohort.cohort_date}>
                      <TableCell className="font-mono">
                        {formatDateLabel(cohort.cohort_date)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {cohort.cohort_size}
                      </TableCell>
                      {[cohort.week_1, cohort.week_2, cohort.week_4, cohort.week_8].map(
                        (pct, index) => (
                          <TableCell
                            key={index}
                            className="text-right font-mono tabular-nums"
                            style={{ color: pct > 0 ? T.green : "var(--muted-foreground)" }}
                          >
                            {pct}%
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyState message="No signup cohorts in this period. Try widening the time range." />
            )}
          </SectionCard>

          <SectionCard
            title="Page Dwell Time"
            subtitle={
              pageDwellTime
                ? `Overall average ${formatDuration(pageDwellTime.overall_avg_dwell_seconds)}`
                : "Average time spent per page"
            }
            icon={Timer}
          >
            {pageDwellTime && pageDwellTime.pages.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Page</TableHead>
                      <TableHead className="text-right">Average</TableHead>
                      <TableHead className="text-right">Median</TableHead>
                      <TableHead className="text-right">Visits</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dwellPageRows.map((page) => (
                      <TableRow key={page.page_path}>
                        <TableCell className="font-mono" title={page.page_path}>
                          {truncatePath(page.page_path, 40)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatDuration(page.avg_dwell_seconds)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                          {formatDuration(page.median_dwell_seconds)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {page.visit_count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <PaginationControls
                  page={dwellPage}
                  pageSize={dwellPageSize}
                  total={dwellTotal}
                  totalPages={dwellTotalPages}
                  onPageChange={setDwellPage}
                  onPageSizeChange={(s) => {
                    setDwellPageSize(s);
                    setDwellPage(1);
                  }}
                />
              </>
            ) : (
              <EmptyState message="No dwell time data yet. Values populate as users navigate between pages." />
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
